import {
  captureError,
  combineErrorContexts,
} from '@leagueplatform/observability';
import { Auth } from '@leagueplatform/auth';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import type { ErrorContext } from '@leagueplatform/observability';
import {
  combineHeaders,
  getAcceptLanguageHeader,
  parseJsonSafe,
  ApiErrorBody,
  getErrorDetail,
} from '../utils/utils';
import { getDevRoutingEnv } from './dev-routing-env';

export interface ConfigFetchOptions extends RequestInit {
  token?: string;
  clientId: string;
  appVersion?: string;
  errorContext?: ErrorContext;
}

const getToken = async (passedToken?: string): Promise<string> => {
  if (passedToken) {
    return passedToken;
  }

  const clientToken = await StandaloneAuth.client.getTokenSilently();
  return clientToken;
};

const getFetchOptions = async ({
  token: passedToken,
  appVersion,
  clientId,
  ...fetchOptions
}: ConfigFetchOptions) => {
  const getCurrentLocale = getAcceptLanguageHeader();
  const tokenToUse = await getToken(passedToken);
  const authorizationHeaders = {
    headers: new Headers({
      Authorization: `Bearer ${tokenToUse}`,
    }),
  };
  const clientIdOptions = {
    headers: new Headers({
      'X-League-Client-Id': clientId,
      ...(appVersion && { 'X-League-Client-Version': appVersion }),
    }),
  };
  const { mirrordRoute, previewRoute } = getDevRoutingEnv();
  const devRoutingHeaders = new Headers();
  if (mirrordRoute) {
    devRoutingHeaders.set('X-League-Mirrord-Route', mirrordRoute);
  }
  if (previewRoute) {
    devRoutingHeaders.set('X-League-Preview-Route', previewRoute);
  }
  const devRoutingOptions = { headers: devRoutingHeaders };
  return {
    ...fetchOptions,
    headers: combineHeaders(
      fetchOptions,
      authorizationHeaders,
      getCurrentLocale,
      clientIdOptions,
      devRoutingOptions,
    ),
  };
};

export const configFetch = async (
  url: string,
  options: ConfigFetchOptions,
  errorContext: ErrorContext = {},
) => {
  if (!Auth.initialized) {
    throw new Error('Auth not initialized');
  }

  const fetchOptions = await getFetchOptions(options);
  const promise = fetch(url, fetchOptions);

  const response = await promise;

  if (!response.ok) {
    const { data: parsedBody, error: bodyParseErrorMessage } =
      await parseJsonSafe<ApiErrorBody>(response.clone());
    const errorDetail = getErrorDetail(parsedBody) ?? 'An error has occurred';
    const fetchErrorContext = combineErrorContexts([
      {
        errorName: 'Network Fetch Error',
        tags: { requestUrl: response.url },
        context: {
          Errors: {
            ...parsedBody?.errors,
          },
          Response: {
            ok: response.ok,
            redirected: response.redirected,
            status: response.status,
            type: response.type,
            url: response.url,
            method: options.method ?? 'GET',
          },
          ...(bodyParseErrorMessage && {
            BodyParseError: { message: bodyParseErrorMessage },
          }),
        },
      },
      errorContext,
    ]);
    captureError(new Error(errorDetail), fetchErrorContext);
  }

  return promise;
};
